import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/use-auth";
import { Link } from "wouter";
import { 
  BriefcaseIcon, 
  Users, 
  FileText, 
  PlusCircle, 
  Clock, 
  BarChart,
  Building,
  MailOpen
} from "lucide-react";

export default function EmployerDashboard() {
  const { user } = useAuth();

  return (
    <DashboardLayout pageTitle="Employer Dashboard">
      {/* Overview Cards */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Active Job Postings</p>
                <h3 className="text-2xl font-bold mt-1">5</h3>
              </div>
              <div className="p-3 bg-primary-100 rounded-full">
                <BriefcaseIcon className="h-6 w-6 text-primary-600" />
              </div>
            </div>
            <div className="flex items-center mt-4 text-xs text-slate-500">
              <Clock className="h-4 w-4 mr-1 text-slate-400" />
              <span>2 positions closing soon</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Applications</p>
                <h3 className="text-2xl font-bold mt-1">43</h3>
              </div>
              <div className="p-3 bg-primary-100 rounded-full">
                <FileText className="h-6 w-6 text-primary-600" />
              </div>
            </div>
            <div className="flex items-center mt-4 text-xs text-slate-500">
              <MailOpen className="h-4 w-4 mr-1 text-slate-400" />
              <span>12 new this week</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Candidates in Pipeline</p>
                <h3 className="text-2xl font-bold mt-1">18</h3>
              </div>
              <div className="p-3 bg-primary-100 rounded-full">
                <Users className="h-6 w-6 text-primary-600" />
              </div>
            </div>
            <div className="flex items-center mt-4 text-xs text-slate-500">
              <Building className="h-4 w-4 mr-1 text-slate-400" />
              <span>5 interview-ready</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Placements YTD</p>
                <h3 className="text-2xl font-bold mt-1">27</h3>
              </div>
              <div className="p-3 bg-primary-100 rounded-full">
                <BarChart className="h-6 w-6 text-primary-600" />
              </div>
            </div>
            <div className="flex items-center mt-4 text-xs text-slate-500">
              <Users className="h-4 w-4 mr-1 text-slate-400" />
              <span>85% retention rate</span>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Quick Actions */}
      <section className="mb-8">
        <Card>
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            <Button asChild className="flex items-center">
              <Link href="/dashboard/employer/post-job">
                <PlusCircle className="mr-2 h-4 w-4" />
                Post a New Job
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link href="/dashboard/employer/applications">
                View Applications
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link href="/dashboard/employer/candidates">
                Browse Candidates
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link href="/dashboard/employer/profile">
                Edit Company Profile
              </Link>
            </Button>
          </CardContent>
        </Card>
      </section>

      {/* Job Listings */}
      <section className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Your Job Postings</h2>
          <Button asChild variant="outline" size="sm">
            <Link href="/dashboard/employer/jobs">View All</Link>
          </Button>
        </div>
        <div className="space-y-4">
          {[
            { title: "Warehouse Associate", location: "Chicago, IL", applications: 15, status: "Active" },
            { title: "Customer Service Representative", location: "Remote", applications: 22, status: "Active" },
            { title: "Delivery Driver", location: "Chicago, IL", applications: 6, status: "Active" },
            { title: "Administrative Assistant", location: "Chicago, IL", applications: 0, status: "New" },
            { title: "Retail Sales Associate", location: "Chicago, IL", applications: 0, status: "Draft" }
          ].map((job, index) => (
            <Card key={index}>
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center">
                      <h3 className="font-medium text-slate-900">{job.title}</h3>
                      <span className={`ml-2 text-xs px-2 py-1 rounded-full ${
                        job.status === "Active" ? "bg-green-100 text-green-800" :
                        job.status === "New" ? "bg-blue-100 text-blue-800" :
                        "bg-slate-100 text-slate-800"
                      }`}>
                        {job.status}
                      </span>
                    </div>
                    <p className="text-sm text-slate-500">{job.location}</p>
                    <p className="text-sm text-slate-500">{job.applications} applications</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline">Edit</Button>
                    <Button size="sm" variant="outline">View</Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Recent Applications */}
      <section>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Recent Applications</h2>
          <Button asChild variant="outline" size="sm">
            <Link href="/dashboard/employer/applications">View All</Link>
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { name: "James Wilson", position: "Warehouse Associate", status: "New", date: "Today" },
            { name: "Maria Garcia", position: "Customer Service Representative", status: "Reviewing", date: "Yesterday" },
            { name: "Robert Johnson", position: "Delivery Driver", status: "Interview", date: "Apr 14" },
            { name: "Emily Chen", position: "Warehouse Associate", status: "New", date: "Apr 13" },
            { name: "David Brown", position: "Customer Service Representative", status: "Offer Extended", date: "Apr 12" },
            { name: "Sarah Lee", position: "Delivery Driver", status: "Interview", date: "Apr 10" }
          ].map((application, index) => (
            <Card key={index}>
              <CardContent className="p-4">
                <div className="mb-2">
                  <p className="font-medium">{application.name}</p>
                  <p className="text-sm text-slate-500">{application.position}</p>
                </div>
                <div className="flex justify-between items-center">
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    application.status === "New" ? "bg-blue-100 text-blue-800" :
                    application.status === "Reviewing" ? "bg-amber-100 text-amber-800" :
                    application.status === "Interview" ? "bg-purple-100 text-purple-800" :
                    "bg-green-100 text-green-800"
                  }`}>
                    {application.status}
                  </span>
                  <span className="text-xs text-slate-500">{application.date}</span>
                </div>
                <Button size="sm" className="w-full mt-3">View Application</Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    </DashboardLayout>
  );
}